<template>
    <div class="search-results-category">
        <div v-if="!isFiltered" class="search-results-category__header">
            <h4 class="search-results-category__title typographies-paragraph-small-bold">
                {{ label }}
            </h4>
            <div class="badge typographies-paragraph-small-bold">{{ total }}</div>
            <CompassPlainButton
                v-if="total > minResults && results.key !== 'archived'"
                :label="$t.__('searchbar.SearchResultsCategory.view_all_results')"
                size="small"
                data-bot="searchbar.view-all-results"
                @click.stop="handleSeeAll"
            />
        </div>
        <div class="search-results-category__results">
            <template v-if="groupBy.length === 0">
                <component
                    :is="componentName(result)"
                    v-for="result in results.items"
                    v-bind="result"
                    :key="result.id"
                    :context="isSimilarResults ? 'similar-results' : ''"
                />
            </template>
            <template v-else>
                <template v-for="group in groupBy">
                    <div
                        v-if="groupedResults[group.key].length"
                        :key="group.key"
                        class="search-results-category__group"
                    >
                        <h5
                            class="search-results-category__group-title typographies-paragraph-small-bold"
                        >
                            {{ group.label }}
                        </h5>
                        <component
                            :is="componentName(result)"
                            v-for="result in groupedResults[group.key]"
                            v-bind="result"
                            :key="result.id"
                            :context="isSimilarResults ? 'similar-results' : ''"
                        />
                    </div>
                </template>
            </template>
            <div v-if="showLimitReached" class="search-results-category__precision">
                <CompassIcon size="small" icon="search" />
                <p class="typographies-paragraph-medium-regular">
                    {{
                        $l10n.__(
                            'Vous avez parcouru les 50 premiers résultats. Pour des résultats plus précis, veuillez affiner votre recherche.',
                        )
                    }}
                </p>
            </div>
        </div>
    </div>
</template>
<script>
import SearchResultsCompany from 'vcomponents/searchbar/results/SearchResultsCompany.vue';
import SearchResultsIndividual from 'vcomponents/searchbar/results/SearchResultsIndividual.vue';
import SearchResultsContact from 'vcomponents/searchbar/results/SearchResultsContact.vue';
import SearchResultsOpportunity from 'vcomponents/searchbar/results/SearchResultsOpportunity.vue';
import SearchResultsItem from 'vcomponents/searchbar/results/SearchResultsItem.vue';
import SearchResultsTicket from 'vcomponents/searchbar/results/SearchResultsTicket.vue';
import SearchResultsDocument from 'vcomponents/searchbar/results/SearchResultsDocument.vue';

export default {
    components: {
        SearchResultsCompany,
        SearchResultsIndividual,
        SearchResultsContact,
        SearchResultsOpportunity,
        SearchResultsItem,
        SearchResultsTicket,
        SearchResultsDocument,
        // For archived declinations, we need SearchResultsDeclination in this list, but we use SearchResultsItem component to display them.
        SearchResultsDeclination: SearchResultsItem,
    },
    props: {
        results: {
            type: Object,
            required: true,
        },
        groupBy: {
            type: Array,
            default: () => [],
        },
        groupedResults: {
            type: Object,
            default: () => {},
        },
        minResults: {
            type: Number,
            default: 3,
        },
        isFiltered: {
            type: Boolean,
            default: false,
        },
        isSimilarResults: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        label() {
            return this.$l10n.__(this.results.label);
        },
        total() {
            return this.results.count;
        },
        componentName() {
            return (item) => {
                if (this.results.key === 'archived') {
                    return `search-results-${item.resultType}`;
                }
                return `search-results-${this.results.key}`;
            };
        },
        showLimitReached() {
            return this.results.items.length >= 50;
        },
    },
    methods: {
        handleSeeAll() {
            this.$emit('see-all', this.results.filters);
        },
    },
};
</script>
<style lang="scss" scoped>
.search-results-category {
    &:not(:first-child) {
        &:before {
            content: '';
            background: var(--colors-common-border-lighter, #e9e9ec);
            display: block;
            height: 1px;
            margin: var(--spacing-0) var(--spacing-3) var(--spacing-3);
        }
    }

    &__header {
        align-items: center;
        display: flex;
        gap: var(--spacing-0);
        min-height: 24px;
        padding: 0 var(--spacing-3);

        :deep(.compass-plain-button) {
            margin-left: auto;
        }
    }

    &__title {
        color: var(--colors-common-text-subtle-default, #565665);
        margin: 0;
    }

    .badge {
        background: var(--colors-common-background-primary-hover-alt);
        border-radius: 50px;
        color: var(--colors-common-text-primary-default, #4868fd);
        padding: 0 6px;
    }

    &__group-title {
        color: var(--colors-common-text-subtle-default, #565665);
        margin: var(--spacing-0) 0 0;
        padding: 0 var(--spacing-3);
        text-transform: uppercase;
    }

    &__precision {
        align-items: center;
        background: var(--colors-common-background-primary-lightest);
        border-radius: var(--spacing-0);
        color: var(--colors-common-text-standard-default);
        display: flex;
        gap: var(--spacing-0);
        margin: var(--spacing-3);
        padding: var(--spacing-1);

        p {
            margin: 0;
        }
    }
}
</style>
